import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Text, useTheme } from '@Sage/hoi-poi-ui';

import ModalHoi from 'components/ModalHoi';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';

import './styles.scss';

const CampaignSendConfirmModal = memo(
    ({
        isConsentChecked,
        isOpen,
        isSubmitting,
        onConsentChange,
        onConfirm,
        onRequestClose,
        recipientsCount,
        scheduleAt,
    }) => {
        const theme = useTheme();
        const formattedRecipientsCount = Number(recipientsCount || 0).toLocaleString();

        const handleConsentRowClick = useCallback(() => {
            if (isSubmitting) {
                return;
            }
            onConsentChange();
        }, [isSubmitting, onConsentChange]);

        const handleConsentCheckboxClick = useCallback((event) => {
            event.stopPropagation();
        }, []);

        return (
            <ModalHoi
                className="campaign-send-confirm-modal"
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                onCancel={onRequestClose}
                onConfirm={onConfirm}
                title={getLiteral('title_marketing_campaigns_send_email_campaign_confirm')}
                cancelText={getLiteral('action_cancel')}
                confirmText={getLiteral('action_send_email_now')}
                isLoading={isSubmitting}
                size="medium"
                useHeader
                useCornerClose
                isConfirmDisabled={isSubmitting || !isConsentChecked}
                buttonsLowercase
            >
                <Text className="campaign-send-confirm-modal__message" type="body">
                    {scheduleAt
                        ? getLiteralWithParameters(
                              'label_marketing_campaigns_send_scheduled_email_campaign_confirm_message',
                              [formattedRecipientsCount],
                          )
                        : getLiteralWithParameters(
                              'label_marketing_campaigns_send_email_campaign_confirm_message',
                              [formattedRecipientsCount],
                          )}
                </Text>
                <div
                    className="campaign-send-confirm-modal__consent"
                    onClick={handleConsentRowClick}
                    role="presentation"
                >
                    <span
                        className="campaign-send-confirm-modal__consent-checkbox"
                        onClick={handleConsentCheckboxClick}
                    >
                        <Checkbox
                            checked={isConsentChecked}
                            color="actionMinor"
                            size="medium"
                            onChange={onConsentChange}
                        />
                    </span>
                    <Text type="body" color={theme.colors.utility.textSecondary}>
                        {getLiteral('label_marketing_campaigns_send_confirm_consent')}
                    </Text>
                </div>
            </ModalHoi>
        );
    },
);

CampaignSendConfirmModal.propTypes = {
    isConsentChecked: PropTypes.bool,
    isOpen: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    onConsentChange: PropTypes.func,
    onConfirm: PropTypes.func,
    onRequestClose: PropTypes.func,
    recipientsCount: PropTypes.number,
    scheduleAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

CampaignSendConfirmModal.displayName = 'CampaignSendConfirmModal';

export default CampaignSendConfirmModal;
